import { useRef, useEffect } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import type { EChartsCoreOption } from "echarts/core";

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  SVGRenderer,
]);

interface EChartProps {
  option: EChartsCoreOption;
  style?: React.CSSProperties;
  onEvents?: Record<string, (params: unknown) => void>;
}

// Lightweight Echarts wrapper to reduce bundle size and avoid React-Echarts dependencies
export function EChart({ option, style, onEvents }: EChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof echarts.init>>(null);
  const onEventsRef = useRef(onEvents);
  onEventsRef.current = onEvents;

  // Initialize once on mount, dispose on unmount
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const chart = echarts.init(el, null, { renderer: "svg" });
    chartRef.current = chart;

    if (onEventsRef.current) {
      for (const [event, handler] of Object.entries(onEventsRef.current)) {
        chart.on(event as any, handler as any);
      }
    }

    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Re-apply option after every render
  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: false });
  });

  return <div ref={containerRef} style={style} />;
}
